import React from "react"
import SectionCard from "../../../shared/components/SectionCard"
import { ui } from "../../../shared/styles/uiSystem"

function EmptyState({ loading, text }) {
  return (
    <div style={{ color: "#64748b", fontSize: 13 }}>
      {loading ? "Cargando..." : text}
    </div>
  )
}

function CollapseButton({ collapsed, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...ui.secondaryButton,
        minWidth: 42,
        minHeight: 36
      }}
    >
      {collapsed ? "+" : "−"}
    </button>
  )
}

function Tag({ children, tone = "neutral" }) {
  const tones = {
    neutral: {
      background: "#f8fafc",
      border: "#e2e8f0",
      color: "#334155"
    },
    accent: {
      background: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8"
    }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: 24,
        padding: "0 8px",
        borderRadius: 999,
        background: visual.background,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 0.2
      }}
    >
      {children}
    </span>
  )
}

function SummaryMetric({ label, value, helper }) {
  return (
    <div
      style={{
        ...ui.softCard,
        padding: 12,
        display: "grid",
        gap: 4,
        alignContent: "start"
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 22,
          lineHeight: 1.05,
          fontWeight: 900,
          color: "#0f172a"
        }}
      >
        {value}
      </div>

      {helper ? (
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            lineHeight: 1.35
          }}
        >
          {helper}
        </div>
      ) : null}
    </div>
  )
}

function ActionCard({ action, index, onOpenAction, getActionCardStyle }) {
  const visual = getActionCardStyle(action.level)
  const targetLabel = getActionTargetLabel(action.actionTarget)
  const levelLabel = getLevelLabel(action.level)

  return (
    <button
      onClick={() => onOpenAction(action)}
      style={{
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        border: visual.border,
        background: visual.background,
        borderRadius: 16,
        padding: 14,
        display: "grid",
        gap: 10,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "flex-start",
          flexWrap: "wrap"
        }}
      >
        <div style={{ display: "grid", gap: 4, minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 800,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: 0.3
            }}
          >
            Acción {index + 1}
          </div>

          <div
            style={{
              fontWeight: 900,
              fontSize: 16,
              lineHeight: 1.25,
              color: visual.color,
              wordBreak: "break-word"
            }}
          >
            {action.title}
          </div>

          <div
            style={{
              fontSize: 12.5,
              color: "#475569",
              fontWeight: 700
            }}
          >
            {action.clinicName || "Sin clínica"}
          </div>
        </div>

        <Tag tone="accent">{levelLabel}</Tag>
      </div>

      <div
        style={{
          color: "#334155",
          fontSize: 13,
          lineHeight: 1.45
        }}
      >
        {action.description}
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap"
        }}
      >
        {!!action.patientId ? <Tag>PACIENTE DIRECTO</Tag> : null}
        <Tag>{targetLabel}</Tag>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          paddingTop: 2
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            fontWeight: 700
          }}
        >
          Abrir recomendación
        </div>

        <div
          style={{
            fontSize: 12.5,
            color: "#0f172a",
            fontWeight: 900
          }}
        >
          Ir a acción →
        </div>
      </div>
    </button>
  )
}

export default function ExecutiveActionsPanel({
  loading,
  actions = [],
  collapsed,
  onToggle,
  onOpenAction,
  getActionCardStyle
}) {
  const rows = Array.isArray(actions) ? actions : []
  const directPatients = rows.filter((item) => Boolean(item?.patientId)).length
  const financialActions = rows.filter(
    (item) => String(item?.actionTarget || "").trim().toUpperCase() === "FINANCIAL"
  ).length

  return (
    <SectionCard
      title="Acciones recomendadas"
      subtitle="Qué conviene hacer hoy"
      actions={
        <CollapseButton
          collapsed={collapsed}
          onClick={onToggle}
        />
      }
    >
      {!collapsed && (
        <>
          {!rows.length ? (
            <EmptyState loading={loading} text="Sin acciones recomendadas por el momento." />
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: 10
                }}
              >
                <SummaryMetric
                  label="Acciones activas"
                  value={formatCount(rows.length)}
                  helper="Recomendaciones visibles hoy"
                />
                <SummaryMetric
                  label="Pacientes directos"
                  value={formatCount(directPatients)}
                  helper="Acciones ligadas a un paciente"
                />
                <SummaryMetric
                  label="Foco financiero"
                  value={formatCount(financialActions)}
                  helper="Acciones dirigidas a cobranza o cartera"
                />
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                {rows.map((action, index) => (
                  <ActionCard
                    key={`${action.title}-${action.clinicId}-${index}`}
                    action={action}
                    index={index}
                    onOpenAction={onOpenAction}
                    getActionCardStyle={getActionCardStyle}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </SectionCard>
  )
}

function getActionTargetLabel(actionTarget) {
  const raw = String(actionTarget || "").trim().toUpperCase()

  if (raw === "FINANCIAL") return "FINANZAS"
  if (raw === "AGENDA") return "AGENDA"
  if (raw === "CASH") return "CAJA"
  if (raw === "PORTFOLIO") return "CARTERA"
  if (raw === "PATIENT") return "PACIENTE"
  if (raw === "DASHBOARD") return "DASHBOARD"
  if (raw === "PROMOTION") return "PROMOCIÓN"
  if (raw === "RECALL") return "RECALL"

  return "ACCIÓN"
}

function getLevelLabel(level) {
  const raw = String(level || "").trim().toUpperCase()

  if (raw === "HIGH" || raw === "ALTA") return "Prioridad alta"
  if (raw === "MEDIUM" || raw === "MEDIA") return "Prioridad media"
  if (raw === "LOW" || raw === "BAJA") return "Prioridad baja"

  return "Prioridad"
}

function formatCount(value) {
  const parsed = Number(value)
  const safe = Number.isFinite(parsed) ? parsed : 0

  return new Intl.NumberFormat("es-MX", {
    maximumFractionDigits: 0
  }).format(safe)
}